import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';

interface Props extends React.ComponentProps<'input'> {
  className?: string;
  disabled?: boolean;
  inputClassName?: string;
  text?: string;
  subtext?: string;
  price?: string;
  onChecked?: () => void;
  checkedClassName?: string;
  checkIconClassName?: string;
  checkBadgeClassName?: string;
  borderClassName?: string;
}

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  onChecked?: () => void;
  checkedClassName?: string;
  checkIconClassName?: string;
  checkBadgeClassName?: string;
  borderClassName?: string;
}

const CustomSelection: React.FC<Props> = ({
  className = '',
  inputClassName = '',
  disabled = false,
  text = '',
  subtext = '',
  price = '',
  onChecked,
  checkedClassName = 'border border-secondary-regular bg-secondary-regular/70 text-white',
  checkIconClassName = 'text-white',
  checkBadgeClassName = 'bg-secondary-regular',
  borderClassName = 'border-neutral-400',
  children,
  checked,
  onChange,
  ...props
}) => {
  const labelClassName = useClassNames([
    `text-13 font-medium flex ${
      disabled ? 'cursor-not-allowed' : 'cursor-pointer'
    } items-center justify-between border ${borderClassName} px-15 py-15`,
    className,
  ]);

  const inputClassNames = useClassNames(['h-12 w-12 border-0 hidden', inputClassName]);

  const subtextClassName = useClassNames(['text-11', checked ? 'text-inherit' : 'font-primaryThin text-muted']);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
    onChecked?.();
  };

  return (
    <label htmlFor={props.id} data-testid="atom-custom-selection">
      <div className="relative h-70">
        <div className="absolute right-0 top-0">
          {checked && (
            <span className={`flex size-24 items-center justify-center rounded-full ${checkBadgeClassName}`}>
              <CheckIcon className={`size-16 ${checkIconClassName}`} strokeWidth={1} aria-hidden />
            </span>
          )}
        </div>
        <div className="static pr-12 pt-12">
          <div data-component-group="text-container" className={`inline-block ${labelClassName} ${checked ? checkedClassName : ''}`}>
            <div className="flex w-full justify-between">
              <div className="flex-col">
                <p className="text-14">{text}</p>
                <p className={subtextClassName} title={subtext}>
                  {subtext}
                </p>
              </div>
              <div>
                <p className="font-primaryThin text-14">{price}</p>
              </div>
            </div>
            <input
              className={`${inputClassNames} ${checked ? 'hidden' : 'bg-site'}`}
              type="radio"
              checked={checked}
              onChange={(e) => handleChange(e)}
              {...props}
            />
            {children}
          </div>
        </div>
      </div>
    </label>
  );
};

export default CustomSelection;
